import { useEffect, useRef } from 'react';
import time from 'utils/time';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';

const EngagementStatsDataTable = ({
  totalSeries,
  breakdown,
  labelFor,
  currentView,
  onCellClick,
  activeCell,
  activeSection,
  setActiveSection,
}) => {
  const containerRef = useRef(null);

  // Mirrors MentionsByTopic's autoScrollFunction: if the hovered column's header falls outside the
  // visible scroll area, bring it into view (shared hover state with the chart drives this too).
  useEffect(() => {
    if (activeSection == null || !containerRef.current) return;
    const headers = containerRef.current.querySelectorAll('.chart-data-info thead th');
    const element = headers[activeSection];
    if (!element) return;
    const { left: containerLeft, right: containerRight } = containerRef.current.getBoundingClientRect();
    const { left, right, width } = element.getBoundingClientRect();
    if (containerLeft >= right - width || containerRight <= left + width) {
      containerRef.current.scrollLeft = element.offsetLeft - 20;
    }
  }, [activeSection]);

  if (!totalSeries?.datapoints?.length) return null;

  const labelDates = totalSeries.datapoints.map((point) => point.date);
  const rows = (breakdown ?? []).map((series) => ({
    bucket: series.bucket,
    label: labelFor(series.bucket),
    valuesByDate: Object.fromEntries((series.datapoints ?? []).map((point) => [point.date, point.value ?? 0])),
  }));

  const formatDate = (date) => time(date).formatGeneralDate(false, currentView === 'Month');

  return (
    <>
      <div className='flex-centered justify-content-between pt-5 pb-4 mb-1 sticky-item'>
        <h3 className='title-h5-m-bold mb-0'>Chart data</h3>
      </div>
      <div className='chart-data-container d-flex align-items-start' onMouseLeave={() => setActiveSection?.(null)}>
        <div className='overflow-auto chart-data-labels-container'>
          <table className='table chart-data-labels'>
            <thead>
              <tr>
                <th className='font-weight-bold'>Type</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.bucket}>
                  <td>
                    <span className='nowrap-item'>{row.label}</span>
                  </td>
                </tr>
              ))}
              <tr>
                <td className='font-weight-bold'>Total</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className='overflow-auto chart-data-info-container flex-grow-1' ref={containerRef}>
          <table className='table chart-data-info chart-sort-table'>
            <thead>
              <tr>
                {labelDates.map((date, index) => (
                  <th
                    key={date}
                    className={`nowrap-item font-weight-bold ${index === activeSection ? 'selected-label-total' : ''}`}
                    onMouseOver={() => setActiveSection?.(index)}
                  >
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.bucket}>
                  {labelDates.map((date, index) => {
                    const value = row.valuesByDate[date] ?? 0;
                    const isClicked = activeCell?.date === date && activeCell?.bucket === row.bucket;
                    return (
                      <td
                        key={date}
                        className={index === activeSection ? 'selected-label-info' : ''}
                        style={isClicked ? { backgroundColor: '#0094CC' } : undefined}
                        onMouseOver={() => setActiveSection?.(index)}
                        onClick={() => onCellClick({ date, bucket: row.bucket, label: row.label, value })}
                      >
                        <span className={`cell-element text-light-blue ${isClicked ? 'text-white font-weight-bold' : ''}`}>
                          {thousandCommas(value)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                {totalSeries.datapoints.map((point, index) => {
                  const isClicked = activeCell?.date === point.date && !activeCell?.bucket;
                  return (
                    <td
                      key={point.date}
                      className={`label-total ${index === activeSection ? 'selected-label-total' : ''}`}
                      style={isClicked ? { backgroundColor: '#0094CC', color: '#fff' } : undefined}
                      onMouseOver={() => setActiveSection?.(index)}
                      onClick={() => onCellClick({ date: point.date, bucket: null, value: point.value ?? 0 })}
                    >
                      {thousandCommas(point.value ?? 0)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default EngagementStatsDataTable;
